import { useState } from "react";
import Navbar from "../Navbar/Navbar";

// ─── DESIGN TOKENS (matches PPCACrashCourse.jsx) ──────────────────
const C = {
  navy:    "#0B1F3A",
  navy2:   "#132d52",
  gold:    "#C9A84C",
  goldL:   "#FDF6E3",
  white:   "#FFFFFF",
  offwhite:"#F8FAFC",
  slate:   "#F1F5F9",
  border:  "#E2E8F0",
  muted:   "#64748B",
  dark:    "#1E293B",
  green:   "#059669",
  greenL:  "#ECFDF5",
  blue:    "#1D4ED8",
  blueL:   "#EFF6FF",
  amber:   "#B45309",
  amberL:  "#FFFBEB",
  red:     "#DC2626",
  redL:    "#FEF2F2",
  purple:  "#7C3AED",
  purpleL: "#F5F3FF",
};

// ─── 4-GROUP FRAMEWORK (Washington = 8-State Group) ───────────────
const GROUPS4 = [
  { id:"pevo",       label:"14 PEVO States",   sub:"Performance-Based",              color:C.green,  colorL:C.greenL,  icon:"🛡️", states:"AL, AR, CO, CT, DE, FL, GA, KY, LA, MI, MS, NC, NM, SC" },
  { id:"eight",      label:"8-State Group",    sub:"Uniform Req. — Washington's Group", color:C.blue, colorL:C.blueL,  icon:"🔷", states:"AZ, ID, IA, KS, MO, NE, NV, UT, WA" },
  { id:"standalone", label:"Stand-Alone States", sub:"Independent Requirements",      color:C.amber,  colorL:C.amberL,  icon:"⭐", states:"CA, OR, TN, TX, WI, WY" },
  { id:null,         label:"Remaining States", sub:"To Complete All 50",             color:C.muted,  colorL:C.slate,   icon:"🗺️", states:"AK, DC, HI, IL, IN, MD, MA, ME, MN, MT, ND, OH, OK, PA, RI, SD, VA, VT, WV, NY, NJ, NH" },
];

// ─── STATE FLAG / EMOJI MAP ────────────────────────────────────────
const STATE_FLAGS = {
  AL:"🌲", AR:"🌾", AZ:"🌵", CA:"🌉", CO:"🏔️", CT:"⚓", DE:"💎",
  FL:"🌴", GA:"🍑", HI:"🌺", ID:"🥔", IL:"🌽", IN:"🏎️", IA:"🌽",
  KS:"🌾", KY:"🏇", LA:"🎷", ME:"🦞", MD:"🦀", MA:"🦃", MI:"🚗",
  MN:"❄️", MS:"🎵", MO:"🏛️", MT:"🦌", NE:"🌾", NV:"🎰", NH:"🍁",
  NJ:"🗽", NM:"🌶️", NY:"🗽", NC:"⭐", ND:"🌻", OH:"✈️", OK:"🛢️",
  OR:"🌲", PA:"🔔", RI:"⚓", SC:"🌴", SD:"🏔️", TN:"🎵", TX:"⭐",
  UT:"⛷️", VT:"🍁", VA:"🏛️", WA:"🌲", WV:"⛏️", WI:"🧀", WY:"🦬",
};

// ─── FULL STATE-LEVEL DATA FOR THE 3 DETAILED GROUPS ───────────────
const GROUP_STATES = {
  pevo: {
    id: "pevo", label: "14 PEVO States", sublabel: "Performance-Based",
    color: C.green, colorL: C.greenL, icon: "🛡️",
    description: "Require demonstrated competency via an approved PPCA/PEVO course. These states accept reciprocal certificates from recognized national providers.",
    keyRule: "PPCA certificate accepted. Carry original cert + permit at all times.",
    states: [
      { code:"AL", name:"Alabama",        width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
      { code:"AR", name:"Arkansas",       width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
      { code:"CO", name:"Colorado",       width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"w/ lighting", cert:"PEVO Cert" },
      { code:"CT", name:"Connecticut",    width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",  cert:"PEVO Cert" },
      { code:"DE", name:"Delaware",       width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"w/ lighting", cert:"PEVO Cert" },
      { code:"FL", name:"Florida",        width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO + Def. Driving" },
      { code:"GA", name:"Georgia",        width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"w/ lighting", cert:"PEVO Cert" },
      { code:"KY", name:"Kentucky",       width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"w/ lighting", cert:"PEVO Cert" },
      { code:"LA", name:"Louisiana",      width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
      { code:"MI", name:"Michigan",       width:"14'6\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",  cert:"PEVO Cert" },
      { code:"MS", name:"Mississippi",    width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
      { code:"NC", name:"North Carolina", width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"w/ lighting", cert:"PEVO Cert" },
      { code:"NM", name:"New Mexico",     width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
      { code:"SC", name:"South Carolina", width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",     cert:"PEVO Cert" },
    ],
  },
  eight: {
    id: "eight", label: "8-State Group", sublabel: "Uniform Requirements — Washington's Group",
    color: C.blue, colorL: C.blueL, icon: "🔷",
    description: "Operate under a shared framework of escort-trigger widths and equipment standards. Washington is the outlier in this group — it additionally requires the state-issued WSDOT Pilot Car Operator Permit on top of these baseline numbers.",
    keyRule: "Uniform equipment standard, but WA also requires its own WSDOT Pilot Car Operator Permit.",
    states: [
      { code:"AZ", name:"Arizona",    width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"ID", name:"Idaho",      width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"w/ lighting", cert:"No cert" },
      { code:"IA", name:"Iowa",       width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"KS", name:"Kansas",     width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"MO", name:"Missouri",   width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"NE", name:"Nebraska",   width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"NV", name:"Nevada",     width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"UT", name:"Utah",       width:"14'6\"", height:"14'6\"", length:"95'0\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
      { code:"WA", name:"Washington", width:"12'0\"", height:"14'0\"", length:"75'0\"", ins:"$500K CSL", night:"No Fri 3pm–Mon 6am", cert:"WSDOT Pilot Car Permit", alert:true },
    ],
  },
  standalone: {
    id: "standalone", label: "Stand-Alone States", sublabel: "Independent Requirements",
    color: C.amber, colorL: C.amberL, icon: "⭐",
    description: "Zero reciprocity. Each state requires its own independent permit or certification obtained before crossing the border — no national cert substitutes.",
    keyRule: "Obtain the state-specific permit BEFORE crossing the border.",
    states: [
      { code:"CA", name:"California", width:"14'0\"", height:"14'0\"", length:"95'0\"",  ins:"$750K CSL", night:"No Fri 3pm–Mon 6am", cert:"CALTRANS Permit", alert:true },
      { code:"OR", name:"Oregon",     width:"14'6\"", height:"15'6\"", length:"95'0\"",  ins:"$750K CSL", night:"Restricted",         cert:"Permit-specific" },
      { code:"TN", name:"Tennessee",  width:"14'6\"", height:"15'0\"", length:"95'0\"",  ins:"$750K CSL", night:"w/ lighting",        cert:"No cert" },
      { code:"TX", name:"Texas",      width:"16'0\"", height:"16'0\"", length:"110'0\"", ins:"$750K CSL", night:"Allowed",            cert:"TxDOT License", alert:true },
      { code:"WI", name:"Wisconsin",  width:"14'6\"", height:"15'0\"", length:"95'0\"",  ins:"$750K CSL", night:"w/ lighting",        cert:"No cert" },
      { code:"WY", name:"Wyoming",    width:"14'6\"", height:"14'6\"", length:"95'0\"",  ins:"$750K CSL", night:"Allowed",            cert:"No cert" },
    ],
  },
};

// ─── EQUIPMENT CHECKLIST DATA ─────────────────────────────────────
const EQUIPMENT = [
  { icon:"☀️", name:"Amber Rotating/Strobe Light", spec:"360° visibility · Visible 500 ft minimum · Required when escorting", ref:"SAE J845 CLASS 1" },
  { icon:"🛑", name:"OVERSIZE LOAD Sign",          spec:"18\" × 84\" minimum · Yellow background · Black retroreflective letters", ref:"MUTCD 6F.56" },
  { icon:"📻", name:"CB Radio",                    spec:"Channel 19 default · Mandatory two-way contact with carrier", ref:"FCC / DOT" },
  { icon:"📱", name:"Two-Way Radio",               spec:"Required in WA for lead & rear escort coordination", ref:"WSDOT" },
  { icon:"🦺", name:"High-Vis Vest (ANSI 2+)",     spec:"Class 2 in vehicle · Class 3 on roadway and ferry decks", ref:"OSHA 1910.132" },
  { icon:"🚩", name:"Red/Orange Flags",            spec:"18\" × 18\" minimum · Load corners · WA requires spare flags", ref:"MUTCD 6E.01" },
  { icon:"🧯", name:"Fire Extinguisher",           spec:"2.5 lb ABC minimum · Securely mounted · Accessible", ref:"NFPA 10" },
  { icon:"➕", name:"First Aid Kit",               spec:"OSHA Class A minimum · Fully stocked · Within expiration", ref:"OSHA 1910.151" },
  { icon:"✋", name:"STOP/SLOW Paddle",            spec:"18\" diameter minimum · MUTCD compliant · Flagging operations", ref:"MUTCD 6E.03" },
  { icon:"🔺", name:"Traffic Control Devices",     spec:"Cones or triangles as required by permit conditions", ref:"DOT 393.95" },
  { icon:"📄", name:"Permit & Documents",          spec:"Original permit · Route plan · Insurance cert · Operator card", ref:"WSDOT" },
];

// ─── MASTER QUICK REFERENCE ────────────────────────────────────────
const QUICKREF = [
  { h:"Federal Dimensions", rows:[["Max width","8'6\" (102 in)"],["Max GVW","80,000 lbs"],["Single axle max","20,000 lbs"],["Tandem axle max","34,000 lbs"],["Max length (single)","65 ft"]] },
  { h:"Washington Escort Triggers", rows:[["Permit required","Over 8'6\""],["One escort (lead)","Over 12'0\""],["Two escorts (lead+rear)","Over 14'0\""],["WSP escort required","Over 16'0\" / 200,000 lbs"],["Height engineering review","Over 14'0\""]] },
  { h:"Equipment Specifications", rows:[["OVERSIZE LOAD sign","18\" × 84\" min"],["Flag size","18\" × 18\" min"],["Amber light visibility","500 ft"],["STOP/SLOW paddle","18\" diameter min"],["Fire extinguisher","2.5 lb ABC"],["Flagger visibility","500 ft"]] },
  { h:"Escort Positioning", rows:[["Lead escort gap (open hwy)","500 ft minimum"],["Lead escort gap (urban)","1,000 ft"],["Rear escort gap","100–300 ft"]] },
  { h:"Weather Restrictions", rows:[["Wind speed limit","35 mph sustained"],["Sail-type load wind limit","25 mph"],["Minimum visibility","500 ft"]] },
  { h:"CB Channels", rows:[["Primary channel","Channel 19"],["Emergency only","Channel 9"]] },
  { h:"Washington Certification", rows:[["Exam passing score","70% (35/50)"],["Exam length","50 questions"],["Exam duration","90 minutes"],["Annual renewal fee","$95"],["Insurance minimum","$500,000 CSL"],["Retake wait","14 days"],["Permit expiration","Midnight, last date"]] },
  { h:"Ferry & Contacts", rows:[["WSF reservation window","48 hours advance"],["Arrive before departure","30 minutes"],["WSDOT CVS","(360) 704-6340"],["Washington State Patrol","*WSP / (360) 596-4000"],["WSF Commercial Vehicle","(206) 515-3952"]] },
];

// ─── MODULE CONTENT (all 14 modules) ──────────────────────────────
const MODULES = [
  {
    num: 1, icon: "🚔", time: "25 min",
    title: "The Pilot/Escort Vehicle Industry in Washington",
    objectives: [
      "Understand the role and legal responsibilities of a Washington pilot/escort vehicle operator",
      "Know the difference between PEVO certification and the WSDOT Pilot Car Permit",
      "Understand Washington's regulatory authority under WSDOT",
      "Know what types of loads require escort in Washington state",
    ],
    blocks: [
      { type: "para", text: "The pilot/escort vehicle operator (PEVO) plays a critical role in the safe movement of oversize and overweight (OS/OW) loads on public highways. In Washington, oversize loads include wind turbine blades, manufactured homes, bridge beams, construction equipment, and industrial machinery — all of which routinely exceed standard legal dimensions." },
      { type: "para", text: "You are the advance warning system and traffic manager for every permitted move you escort — protecting the public, the carrier, the load owner, and yourself from the liability created by an oversize load." },
      { type: "para", text: "Washington vs. Federal Authority: federal law (23 USC 127 and 49 USC 31111–31114) sets maximum legal vehicle dimensions on the Interstate Highway System. Washington cannot set higher limits on Interstates, but holds full authority over permitting and certification on state highways, county roads, and local streets — administered through WSDOT Commercial Vehicle Services (CVS)." },
      { type: "key", title: "Key Point", text: "Washington is one of 8 states requiring a state-specific permit (WSDOT Pilot Car Operator Permit) IN ADDITION to PEVO certification. Operating without the WSDOT permit is illegal — PEVO alone is not sufficient." },
      { type: "key", title: "Key Point", text: "Washington State Patrol (WSP) escort is required for loads over 16 feet wide OR over 200,000 lbs gross weight — separate from and in addition to standard pilot car escorts." },
      { type: "para", text: "What requires an escort in Washington: width over 12 ft → one lead escort · width over 14 ft → lead and rear escorts · width over 16 ft → WSP escort in addition to pilot cars · height over 14 ft → engineering review · length over 75 ft (combo) → rear escort on two-lane highways · weight over 200,000 lbs → WSP escort required." },
      { type: "warn", title: "Warning", text: "Operating without a valid WSDOT Pilot Car Operator Permit can result in fines, permit revocation, and personal liability if an accident occurs during the move." },
      { type: "tip", title: "Exam Tip", text: "Memorize the three escort trigger widths: over 12 ft (one lead escort), over 14 ft (lead and rear escorts), over 16 ft (adds a mandatory WSP escort)." },
    ],
    practice: [
      { q: "At what load width does Washington require a WSP escort in addition to pilot car escorts?", options: ["Over 12 feet", "Over 14 feet", "Over 16 feet", "Over 18 feet"], correct: 2, explanation: "Washington requires a WSP escort for loads over 16 feet wide or over 200,000 lbs gross weight." },
      { q: "Which agency administers OS/OW permitting and pilot car certification in Washington?", options: ["FHWA", "Washington State Patrol (WSP)", "WSDOT Commercial Vehicle Services (CVS)", "National Pilot Car Association (NPCA)"], correct: 2, explanation: "WSDOT Commercial Vehicle Services (CVS) administers all OS/OW permitting and the Washington Pilot Car Operator certification program." },
    ],
  },
  {
    num: 2, icon: "📏", time: "30 min",
    title: "Federal Size & Weight Regulations",
    objectives: [
      "Know the federal maximum dimensions for all measurement categories",
      "Understand the Bridge Formula and its application in Washington",
      "Know how Washington state limits compare to federal limits",
      "Understand permit requirement thresholds for Washington routes",
    ],
    blocks: [
      { type: "para", text: "Federal law sets the legal baseline on U.S. highways without a permit: 8'6\" wide, 65 ft long single unit (75 ft twin-trailer), 80,000 lbs gross vehicle weight, 20,000 lbs single axle, 34,000 lbs tandem axle." },
      { type: "para", text: "Washington uses these federal limits as a baseline but layers its own permit and escort-trigger thresholds on top for state routes." },
      { type: "key", title: "Key Point", text: "Federal max width: 8'6\". Washington escort triggers: 12 ft (one escort), 14 ft (two escorts), 16 ft (WSP required). These four width thresholds must be known cold." },
      { type: "para", text: "The Bridge Formula in Washington: Washington has adopted the federal Bridge Formula (Formula B) for all state highways to limit weight distributed across multiple axles. Washington also layers on bridge-specific limits: the SR-520 floating bridge caps gross weight at approximately 105,000 lbs, and the I-90 floating bridge caps gross weight at approximately 130,000 lbs in designated lanes, with engineering certification required above 80,000 lbs." },
      { type: "key", title: "Key Point", text: "Floating bridge weight limits for SR-520 and I-90 are not printed on a standard permit — they must be verified separately with WSDOT engineering before any heavy-load move." },
      { type: "warn", title: "Warning", text: "Washington's floating bridge infrastructure is unique in the country. Expect exam questions that specifically test SR-520 and I-90 weight restrictions." },
      { type: "tip", title: "Exam Tip", text: "Washington's permit trigger width on state routes matches the federal standard of 8'6\", but escort requirements don't begin until 12 feet. Don't confuse the permit trigger with the escort trigger." },
    ],
    practice: [
      { q: "What is the maximum gross weight allowed on the SR-520 floating bridge without special engineering authorization?", options: ["80,000 lbs", "100,000 lbs", "105,000 lbs", "No weight restriction applies"], correct: 2, explanation: "The SR-520 floating bridge has a maximum permitted gross weight of approximately 105,000 lbs." },
      { q: "What load width triggers the requirement for a rear escort on two-lane highways?", options: ["Over 8'6\"", "Over 12'0\"", "Over 14'0\"", "Over 16'0\""], correct: 2, explanation: "Washington requires a lead escort at over 12 feet and both lead and rear escorts at over 14 feet on two-lane highways." },
    ],
  },
  {
    num: 3, icon: "🔧", time: "35 min",
    title: "Vehicle & Equipment Requirements",
    objectives: [
      "Know all required equipment for a Washington escort vehicle",
      "Understand Washington-specific equipment requirements beyond federal standards",
      "Know OVERSIZE LOAD sign specifications",
      "Understand amber light and safety vest requirements in Washington",
    ],
    blocks: [
      { type: "para", text: "Every escort vehicle in Washington follows the federal FHWA equipment baseline, with several state-specific additions layered on top — particularly for ferry crossings and mountain pass routes." },
      { type: "para", text: "Required equipment at a glance: amber rotating/strobe lights visible at 500 ft minimum · OVERSIZE LOAD sign at least 18\"×84\", yellow background, black retroreflective letters · CB radio defaulting to Channel 19 · red/orange flags at least 18\"×18\" · ANSI Class 2 vest inside the vehicle, Class 3 on the roadway · 2.5 lb ABC fire extinguisher · OSHA Class A first aid kit · WSDOT Pilot Car Operator Permit card, available for inspection at all times." },
      { type: "key", title: "Key Point", text: "Washington requires the OVERSIZE LOAD sign to also be displayed on the rear of the load for widths over 14 feet, in addition to the standard front placement." },
      { type: "warn", title: "Warning", text: "On Washington State Ferry decks, ANSI Class 3 high-visibility apparel is required regardless of vehicle position, amber lights must remain active throughout the crossing, and you must coordinate directly with WSF crew for vehicle positioning." },
      { type: "para", text: "Mountain pass & ferry additions: between October and April, escort vehicles on SR-20, US-2, or US-12 must carry tire chains and be ready to install them. Operators must also carry spare flags sufficient to re-flag the load if flags are lost or damaged during the move." },
      { type: "tip", title: "Exam Tip", text: "Washington adds ferry-specific equipment rules not found in most state exams — expect at least one question on escort conduct aboard Washington State Ferries." },
    ],
    practice: [
      { q: "What ANSI vest class is required on a Washington State Ferry deck during loading?", options: ["Class 1", "Class 2", "Class 3", "No vest required on ferries"], correct: 2, explanation: "Washington requires ANSI Class 3 high-visibility apparel on all Washington State Ferry decks during loading/unloading, regardless of vehicle position." },
      { q: "For loads wider than 14 feet, where else must the OVERSIZE LOAD sign be displayed?", options: ["On the lead escort vehicle only", "On the rear of the oversize load", "On both escort vehicles", "No additional placement required"], correct: 1, explanation: "Washington requires the OVERSIZE LOAD sign on the rear of the load for widths over 14 feet." },
    ],
  },
  {
    num: 4, icon: "📄", time: "30 min",
    title: "Reading & Understanding Washington Permits",
    objectives: [
      "Identify all key components of a WSDOT oversize permit",
      "Understand Washington's move window restrictions and expiration rules",
      "Know how to handle permit discrepancies before and during a move",
      "Understand the escort operator's legal obligations under a Washington permit",
    ],
    blocks: [
      { type: "para", text: "A WSDOT oversize permit is a legal document that must be reviewed in full before departure. It specifies load information (dimensions, description, configuration), the authorized route including lane restrictions, the exact move window, escort requirements, ferry authorization if applicable, and any special conditions such as bridge restrictions or nighttime authorization." },
      { type: "key", title: "Key Point", text: "Washington oversize permits expire at midnight on the last authorized date — a hard deadline with no grace period, no phone extension, and no exception." },
      { type: "key", title: "Key Point", text: "Ferry crossings must be explicitly authorized within the permit itself. A permit that does not mention a ferry crossing does not authorize boarding a Washington State Ferry." },
      { type: "warn", title: "Warning", text: "If a move extends past midnight on the permit's expiration date, you are operating illegally. Stop the move immediately, secure the load safely, and contact WSDOT for a new permit before continuing." },
      { type: "para", text: "Renewals and cancellations: if a move is delayed past expiration due to weather, breakdown, or a route change, a new permit must be obtained from WSDOT — often online at wsdot.wa.gov — before resuming. WSDOT does not issue refunds for cancelled moves." },
      { type: "tip", title: "Exam Tip", text: "Washington's midnight expiration rule and its explicit ferry-authorization requirement are state-specific and frequently tested." },
    ],
    practice: [
      { q: "At what time do Washington oversize permits expire on the last authorized date?", options: ["5:00 PM", "10:00 PM", "Midnight (12:00 AM)", "The following morning at 6:00 AM"], correct: 2, explanation: "Washington oversize permits expire at midnight on the last authorized date. There is no grace period." },
      { q: "A permit does not mention ferry crossings. Can the escort proceed onto a Washington State Ferry?", options: ["Yes — ferries are public transportation", "Yes — if the route crosses water, ferries are implied", "No — ferry crossings must be explicitly authorized in the permit", "Only if the load is under 12 feet wide"], correct: 2, explanation: "Washington State Ferry crossings must be explicitly authorized in the WSDOT oversize permit." },
    ],
  },
  {
    num: 5, icon: "🗺️", time: "30 min",
    title: "Route Planning & Surveys in Washington",
    objectives: [
      "Understand Washington-specific route survey requirements",
      "Know how to check WSDOT road and mountain pass conditions",
      "Understand ferry route planning procedures",
      "Know bridge clearance verification for floating bridges",
    ],
    blocks: [
      { type: "para", text: "Route surveys should be completed as close to move day as possible — ideally within 24 hours — because conditions shift quickly with weather, ferry schedules, and mountain pass status. A thorough survey checks bridge clearances, floating bridge weight limits, overhead utilities, active construction, urban traffic signal timing, and ferry terminal conditions." },
      { type: "para", text: "Three critical mountain passes: US-2 Stevens Pass and US-12 White Pass both carry avalanche and severe-weather closure risk and can close with less than 30 minutes' notice in winter. SR-20 North Cascades Highway closes seasonally from approximately late November to mid-April, and oversize moves are never permitted during that closure window." },
      { type: "warn", title: "Warning", text: "SR-20 North Cascades Highway closes seasonally each year. Oversize moves are not permitted during the closure — always verify the exact current-year dates at wsdot.wa.gov before planning a route over the pass." },
      { type: "key", title: "Key Point", text: "Contact Washington State Ferries (WSF) Commercial Vehicle reservations at least 48 hours before any ferry crossing at (206) 515-3952, and obtain written confirmation of the reservation." },
      { type: "note", title: "Note", text: "Washington State Ferries reserve the right to deny boarding to any oversize load if sea state or vessel capacity cannot safely accommodate it. Always build contingency time into a route plan that includes a ferry crossing." },
      { type: "tip", title: "Exam Tip", text: "Washington route-survey questions typically center on ferry coordination and mountain pass conditions. Know the 48-hour WSF reservation requirement and that boarding denial is always possible." },
    ],
    practice: [
      { q: "How far in advance should a Washington escort operator contact WSF Commercial Vehicle reservations?", options: ["Same day is fine", "24 hours in advance", "48 hours in advance minimum", "1 week in advance"], correct: 2, explanation: "Washington State Ferries require oversize load reservations at least 48 hours in advance." },
      { q: "Which Washington mountain pass closes seasonally and bars oversize moves during closure?", options: ["US-2 Stevens Pass", "US-12 White Pass", "SR-20 North Cascades Highway", "I-90 Snoqualmie Pass"], correct: 2, explanation: "SR-20 North Cascades Highway closes seasonally, approximately late November to mid-April." },
    ],
  },
  {
    num: 6, icon: "✅", time: "25 min",
    title: "Pre-Trip Inspection in Washington",
    objectives: [
      "Complete a thorough Washington-specific pre-trip inspection",
      "Verify load dimensions and permit compliance before departure",
      "Establish communication protocols with the carrier driver",
      "Understand the legal importance of pre-trip documentation in Washington",
    ],
    blocks: [
      { type: "para", text: "WSDOT regulations require every escort operator to verify functioning equipment and confirm load dimensions against the permit before departure. Document the inspection — in the event of an enforcement stop or accident, WSDOT will expect evidence of pre-trip compliance." },
      { type: "para", text: "Pre-trip checklist highlights: confirm the WSDOT Pilot Car Operator Permit card is in the vehicle, test amber lights for 500-foot visibility, verify the OVERSIZE LOAD sign and flags are undamaged, confirm CB Channel 19 range with the carrier driver, and measure actual load width, height, and length against the permit." },
      { type: "key", title: "Key Point", text: "Your WSDOT Pilot Car Operator Permit card must be physically present in the vehicle and available for inspection at every stop — operating without it present is a violation even if your permit is current." },
      { type: "para", text: "Mountain pass preparation: for routes on US-2, US-12, or SR-20 between October and April, tire chains must be in the vehicle before departure, and current WSDOT road conditions should be checked at wsdot.wa.gov/travel." },
      { type: "warn", title: "Warning", text: "If load dimensions do not match the permit — even by one inch — do not proceed. Contact WSDOT to amend the permit before the move begins; operating on a mismatched permit voids all coverage." },
      { type: "tip", title: "Exam Tip", text: "Pre-trip questions on the Washington exam often test the permit-card requirement and the October–April mountain pass chain requirement — both unique to this state." },
    ],
    practice: [
      { q: "Which document must be physically present in the escort vehicle during a permitted move?", options: ["A printout of the WSDOT website", "WSDOT Pilot Car Operator Permit card", "NPCA certification card", "The carrier's overweight permit only"], correct: 1, explanation: "The WSDOT Pilot Car Operator Permit card must be physically present in the escort vehicle and available for inspection at all times." },
    ],
  },
  {
    num: 7, icon: "🚩", time: "35 min",
    title: "MUTCD Flagging Standards",
    objectives: [
      "Know MUTCD Chapter 6 flagging requirements as adopted in Washington",
      "Know all STOP/SLOW paddle specifications and hand signals",
      "Understand flagger positioning requirements on Washington roads",
      "Know Washington-specific flagging rules for ferry operations",
    ],
    blocks: [
      { type: "para", text: "Washington has adopted the MUTCD together with a Washington State Supplement maintained by WSDOT; where the state supplement is more restrictive, it takes precedence over the federal MUTCD. The moment you control traffic flow during a permitted move, you become a flagger under MUTCD Chapter 6 and are legally responsible for the safety of every road user in your control zone." },
      { type: "para", text: "Paddle & signal specifications: the STOP/SLOW paddle must be at least 18 inches in diameter. The STOP face is octagonal with a red retroreflective background and white letters; the SLOW face is rectangular with an orange background and black letters. Flaggers must remain visible from at least 500 feet." },
      { type: "para", text: "Advance warning on two-lane roads: Washington requires a W20-7a \"FLAGGER AHEAD\" sign placed before the flagger position — this supplements, and never replaces, the flagger." },
      { type: "warn", title: "Warning", text: "On Washington State Ferry decks, WSF crew authority supersedes your escort authority. Never position a STOP/SLOW paddle in a way that conflicts with WSF crew direction, and use reduced-motion signals appropriate to the confined deck space." },
      { type: "key", title: "Key Point", text: "On a ferry deck, follow all WSF crew vehicle-positioning instructions, keep amber lights active throughout the crossing unless told otherwise, and never use signals in a way that could alarm passengers or interfere with crew." },
      { type: "tip", title: "Exam Tip", text: "The STOP face is octagonal, matching a standard stop sign — this is the most commonly missed detail on flagging sections of state exams. Don't confuse it with the rectangular SLOW face." },
    ],
    practice: [
      { q: "On a Washington State Ferry deck, whose authority supersedes the escort operator's traffic control signals?", options: ["The carrier driver's authority", "Washington State Patrol authority", "Washington State Ferries crew authority", "The lead escort vehicle operator's authority"], correct: 2, explanation: "On Washington State Ferry decks, WSF crew authority supersedes all other direction, including pilot car escort signals." },
      { q: "What is the minimum size for a STOP/SLOW flagging paddle under MUTCD standards?", options: ["12 inches", "16 inches", "18 inches", "24 inches"], correct: 2, explanation: "MUTCD requires a minimum 18-inch STOP/SLOW paddle for all flagging operations." },
    ],
  },
  {
    num: 8, icon: "📻", time: "25 min",
    title: "Communication Protocols in Washington",
    objectives: [
      "Know CB radio channel assignments for Washington operations",
      "Understand Washington lead/rear escort communication requirements",
      "Know Washington State Patrol communication protocols",
      "Understand ferry crossing communication requirements",
    ],
    blocks: [
      { type: "para", text: "CB radio communication is mandatory for all Washington escort operations, following federal FHWA guidelines with Washington-specific additions for WSP and WSF coordination. Channel 19 is the default for all Washington escort operations unless the permit specifies otherwise; Channel 9 is emergency-only, monitored by REACT teams and some WSP units." },
      { type: "para", text: "Pre-departure checklist: establish Channel 19 contact with the carrier driver, confirm the channel with the rear escort if applicable, test radio range over the initial route segment, agree on stop code words, and confirm WSP escort contact frequency if a WSP escort is required." },
      { type: "key", title: "Key Point", text: "WSP authority supersedes all escort operator authority. If WSP calls a stop or changes the route, all escorts comply immediately without question." },
      { type: "warn", title: "Warning", text: "For WSP-escorted moves, the move cannot begin until the WSP officer gives explicit authorization at the pre-move briefing. Starting without WSP authorization is a serious violation." },
      { type: "para", text: "Ferry communication: before boarding, contact the WSF terminal to announce the oversize load and confirm the loading lane. During the crossing, maintain radio contact with the carrier driver and follow WSF crew positioning immediately. On disembarkation, follow the WSF unloading sequence and re-establish full convoy communication before departing the terminal." },
      { type: "tip", title: "Exam Tip", text: "Know when WSP is required (over 16 ft wide OR over 200,000 lbs) and that WSP authority overrides escort operator decisions." },
    ],
    practice: [
      { q: "For a Washington move requiring WSP escort, when may the convoy begin moving?", options: ["When all escorts are in position", "When the carrier driver is ready", "Only after the WSP officer gives explicit authorization at the pre-move briefing", "As soon as the permit is verified"], correct: 2, explanation: "WSP-escorted moves in Washington cannot begin until the WSP officer gives explicit authorization at the pre-move briefing." },
    ],
  },
  {
    num: 9, icon: "🚗", time: "30 min",
    title: "Trip Operations — Lead Escort in Washington",
    objectives: [
      "Know lead escort duties and positioning in Washington",
      "Understand Washington-specific intersection clearance procedures",
      "Know how to handle urban corridor moves in Seattle and Tacoma",
      "Understand lead escort responsibilities on Washington ferries",
    ],
    blocks: [
      { type: "para", text: "The lead escort is the advance warning system for the load. Positioning: minimum 500 feet ahead of the load on open highway. In Seattle, Tacoma, and Spokane urban corridors, increase the gap to 1,000 feet to allow adequate clearance time. On mountain passes, maintain visual contact with the road ahead and confirm safe conditions before the load enters a steep downgrade." },
      { type: "para", text: "Intersection clearance: confirm all conflicting traffic is stopped, radio the carrier \"Intersection clear — proceed when ready,\" hold position until the rear of the load clears, then radio the rear escort and resume the 500-foot gap." },
      { type: "para", text: "Urban corridors: Seattle downtown restricts oversize moves to off-peak hours (after 9 PM or before 5 AM) due to narrow lanes and pedestrian traffic. Tacoma Narrows has variable lane widths — confirm lane widths accommodate load width before entering. The I-5 corridor has frequently changing construction zones — check WSDOT's real-time construction map before departure." },
      { type: "key", title: "Key Point", text: "At Washington State Ferry terminals, arrive at least 30 minutes before departure, check in with the WSF terminal agent, board first in the designated lane, and position on the deck only as directed by WSF crew." },
      { type: "warn", title: "Warning", text: "In Seattle downtown and other urban corridors, WSDOT may restrict move timing to off-peak hours. Verify timing restrictions in the permit before planning the move window." },
      { type: "tip", title: "Exam Tip", text: "Washington lead escort questions often focus on ferry procedures — arrive 30 minutes early, board first, WSF crew directs position." },
    ],
    practice: [
      { q: "How early should the lead escort arrive at a ferry terminal before scheduled departure?", options: ["10 minutes", "15 minutes", "30 minutes minimum", "The day before"], correct: 2, explanation: "Lead escorts with ferry crossings must arrive at least 30 minutes before scheduled departure." },
    ],
  },
  {
    num: 10, icon: "🚙", time: "25 min",
    title: "Trip Operations — Rear Escort in Washington",
    objectives: [
      "Know rear escort positioning and duties in Washington",
      "Understand rear escort responsibilities on two-lane Washington highways",
      "Know how rear escorts manage ferry disembarkation",
      "Understand rear escort coordination with WSP on escorted moves",
    ],
    blocks: [
      { type: "para", text: "The rear escort protects the load from behind and reports on conditions behind the load. Positioning: 100 to 300 feet behind the trailing end of the load on open highway; on narrow two-lane rural highways (eastern Washington, mountain routes), closer positioning of 100 feet is recommended to more effectively deter overtaking traffic." },
      { type: "para", text: "Rear escort duties: monitor the load for shift, loose straps, or debris; deter following traffic from passing using amber lights, the OVERSIZE LOAD sign, and CB; on two-lane roads, radio the lead when clear pull-over sections appear; monitor overhead clearance from the best rear vantage point; on mountain passes, watch for following vehicles attempting to pass on downgrades." },
      { type: "key", title: "Key Point", text: "On Washington State Ferries: the rear escort loads LAST onto the ferry and disembarks FIRST. This sequencing ensures the convoy can reassemble correctly on the departure road." },
      { type: "note", title: "Note", text: "On mountain pass routes, the rear escort plays a critical role watching load height clearance at overhead obstructions — the rear position provides the best visibility of height clearance from behind." },
      { type: "tip", title: "Exam Tip", text: "Ferry loading and disembarkation order is Washington-specific and commonly tested: lead escort boards FIRST, rear escort boards LAST; rear disembarks FIRST to get ahead of traffic." },
    ],
    practice: [
      { q: "On a ferry crossing with two escort vehicles, in what order do the escorts load onto the ferry?", options: ["Lead escort last, rear escort first", "Lead escort first, then load, then rear escort last", "Both escorts board simultaneously before the load", "Rear escort first, lead escort last"], correct: 1, explanation: "The lead escort boards first, then the oversize load, and the rear escort boards last. Rear escort disembarks first on arrival." },
    ],
  },
  {
    num: 11, icon: "🚨", time: "30 min",
    title: "Safety & Emergency Procedures in Washington",
    objectives: [
      "Know Washington wind, weather, and visibility restrictions",
      "Understand breakdown procedures for Washington oversize loads",
      "Know Washington-specific accident reporting requirements",
      "Understand when and how to abort a move in Washington",
    ],
    blocks: [
      { type: "para", text: "Wind restrictions: most Washington permits prohibit moves when sustained winds exceed 35 mph. Tall or sail-type loads (manufactured homes, large panels, enclosed trailers) typically have a lower wind limit of 25 mph specified in the permit. Mountain pass wind speeds can be significantly higher than valley conditions — check pass-specific conditions before departure." },
      { type: "para", text: "Visibility: moves must be suspended when visibility falls below 500 feet due to fog, rain, or smoke. Snow and ice: all oversize moves are suspended when roads have accumulated snow or ice; mountain passes require confirmed bare/wet pavement before authorization." },
      { type: "note", title: "Note", text: "Washington State Ferries can deny oversize load boarding based on sea state conditions — this decision is final and cannot be overridden by WSDOT permit or carrier authorization." },
      { type: "para", text: "Breakdown procedures: activate all warning lights, move to the nearest safe pull-off or shoulder (on mountain passes, move to the next available pullout — never block the pass), place warning triangles at 100/200/300 feet, contact the carrier's emergency number, contact WSDOT CVS at (360) 704-6340, and if on a state highway or Interstate contact WSP at *WSP or (360) 596-4000. Remain with the load until properly relieved." },
      { type: "key", title: "Key Point", text: "Washington weather restriction: most permits prohibit moves at sustained winds over 35 mph; sail-type loads may have a 25 mph limit." },
      { type: "warn", title: "Warning", text: "On mountain passes, if a breakdown occurs, move to the next available pullout — do not block the pass. Call WSP (*WSP) immediately." },
      { type: "tip", title: "Exam Tip", text: "Know both key contacts: WSP (*WSP / 360-596-4000) and WSDOT CVS (360-704-6340), and when to use each." },
    ],
    practice: [
      { q: "At what sustained wind speed do most Washington oversize permits require the move to be suspended?", options: ["25 mph", "35 mph", "45 mph", "55 mph"], correct: 1, explanation: "Most Washington oversize permits suspend moves at sustained winds exceeding 35 mph; sail-type loads may have a lower 25 mph threshold." },
      { q: "What is the WSP contact for non-emergency assistance during a breakdown?", options: ["911", "*WSP or (360) 596-4000", "(360) 704-6340 (WSDOT CVS)", "(206) 515-3952 (WSF)"], correct: 1, explanation: "Washington State Patrol non-emergency contact is *WSP (from a cell phone) or (360) 596-4000." },
    ],
  },
  {
    num: 12, icon: "📋", time: "20 min",
    title: "Post-Trip Procedures & Documentation in Washington",
    objectives: [
      "Complete a thorough Washington post-trip inspection",
      "Know Washington documentation and retention requirements",
      "Understand WSDOT incident reporting obligations",
      "Know how to handle permit close-out in Washington",
    ],
    blocks: [
      { type: "para", text: "After every move, inspect equipment (lights, sign, radios, flags, permit card, fire extinguisher gauge) and complete Washington-required documentation: record move start/end time, the actual route traveled with any deviations, any incidents or near-misses, and road hazards for future surveys. Retain copies of all permits for a minimum of 3 years." },
      { type: "warn", title: "Warning", text: "Any accident during a Washington permitted move must be reported to WSDOT CVS within 24 hours — even if there are no injuries and even if WSP was already notified. Both notifications are required." },
      { type: "key", title: "Key Point", text: "Washington requires permit documentation and inspection records to be retained for a minimum of 3 years." },
      { type: "note", title: "Note", text: "Your WSDOT Pilot Car Operator Permit renews annually. Track the expiration date during post-trip documentation to ensure you are never caught with an expired permit." },
    ],
    practice: [
      { q: "Within what timeframe must an accident during a Washington oversize move be reported to WSDOT CVS?", options: ["Immediately (at the scene)", "Within 24 hours", "Within 72 hours", "Within 7 days"], correct: 1, explanation: "Accidents during Washington permitted moves must be reported to WSDOT CVS within 24 hours." },
    ],
  },
  {
    num: 13, icon: "🎓", time: "25 min",
    title: "Washington WSDOT Certification",
    objectives: [
      "Understand the WSDOT Pilot Car Operator Permit process",
      "Know Washington certification renewal requirements and fees",
      "Know exam registration procedures and format",
      "Understand consequences of operating without WSDOT certification in Washington",
    ],
    blocks: [
      { type: "para", text: "Eligibility: valid driver's license from any state (Washington does not require a WA license), minimum age 18, proof of liability insurance ($500,000 CSL minimum), no DUI convictions within 3 years, passing score on the Washington Pilot Car Operator exam (70% minimum), and a $95 annual application fee." },
      { type: "key", title: "Key Point", text: "Washington accepts a driver's license from ANY state — you do not need a Washington state license to obtain a WSDOT Pilot Car Operator Permit." },
      { type: "para", text: "Exam format: 50 multiple-choice questions, 70% minimum passing score (35/50 correct), 90-minute duration, covering Washington regulations, federal FHWA/MUTCD, equipment, ferry protocols, mountain pass procedures, and safety. Retakes are allowed after a 14-day waiting period — no additional waiting period for a third attempt." },
      { type: "warn", title: "Warning", text: "The WSDOT permit expires on the anniversary date with no grace period. Operating with an expired WSDOT permit is illegal regardless of PEVO status." },
      { type: "tip", title: "Exam Tip", text: "Know the WA-specific numbers: 70% passing score, 50 questions, $95 annual fee, $500,000 insurance minimum, 14-day retake wait." },
    ],
    practice: [
      { q: "What is the minimum passing score for the Washington WSDOT Pilot Car Operator exam?", options: ["65%", "70%", "75%", "80%"], correct: 1, explanation: "The Washington WSDOT exam requires a minimum passing score of 70% (35 of 50 questions correct)." },
      { q: "How long must a candidate wait before retaking the exam after a failed attempt?", options: ["7 days", "14 days", "30 days", "60 days"], correct: 1, explanation: "Washington allows exam retakes after a 14-day waiting period." },
    ],
  },
  {
    num: 14, icon: "📝", time: "40 min",
    title: "Final Exam Prep & Washington Practice",
    objectives: [
      "Review all Washington-specific critical numbers and specifications",
      "Practice full-length Washington exam questions",
      "Identify Washington-specific trick questions and common errors",
      "Build exam-day confidence with Washington WSDOT exam strategy",
    ],
    blocks: [
      { type: "para", text: "The Washington WSDOT exam consistently tests specific numerical values — review the Quick Reference tab for the full master list before test day." },
      { type: "key", title: "Key Point", text: "Read every Washington exam question twice. Watch for 'WSDOT,' 'WSP,' and 'WSF' — these three agencies have different authorities, and the exam tests which applies in each scenario." },
      { type: "para", text: "Common trick questions: PEVO certification is NOT sufficient alone in Washington — the WSDOT permit is required in addition. You cannot switch to a different ferry without a route amendment. Permits expire at midnight, not end of business day. On a ferry deck, WSF crew authority always supersedes the escort operator's. SR-20 is NOT open year-round — it closes seasonally. WSP-escorted moves require explicit WSP authorization before starting, regardless of carrier readiness." },
      { type: "warn", title: "Warning", text: "If you fail, Washington allows a retake after only 14 days. Use that time to review the Washington-specific modules (4, 5, 9, 10, 13) — these cover the ferry, WSP, and certification content most candidates miss." },
      { type: "tip", title: "Exam Tip", text: "Washington-specific questions (ferry protocol, WSP escort requirements, permit midnight expiration, SR-20 seasonal closure) are worth the most study time — they cannot be answered from federal FHWA knowledge alone." },
    ],
    practice: [
      { q: "True or False: A pilot car operator holding national PEVO certification can legally operate as an escort in Washington without any additional state certification.", options: ["True — PEVO is nationally recognized", "False — Washington requires the WSDOT Pilot Car Operator Permit in addition to PEVO", "True — for loads under 14 feet wide only", "False — Washington does not recognize PEVO at all"], correct: 1, explanation: "Washington is one of 8 states requiring a state-specific permit. The WSDOT Pilot Car Operator Permit is required regardless of PEVO status." },
      { q: "SR-20 North Cascades Highway is closed to oversize moves during which period?", options: ["Only during active snowfall events", "Every weekend from October to May", "Seasonally from approximately late November to mid-April", "Only on holidays and major travel days"], correct: 2, explanation: "SR-20 closes seasonally from approximately late November to mid-April due to snow and avalanche conditions." },
      { q: "A Washington oversize permit is dated through Thursday. At what time does it expire?", options: ["5:00 PM Thursday (end of business)", "Midnight Thursday", "Friday 6:00 AM", "It does not expire until the move is complete"], correct: 1, explanation: "Washington oversize permits expire at midnight on the last authorized date." },
    ],
  },
];

// ─── MICRO COMPONENTS ──────────────────────────────────────────────

function SectionEyebrow({ text }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 16px" }}>
      <div style={{ flex: 1, height: 1, background: C.border }} />
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: C.muted }}>{text}</span>
      <div style={{ flex: 1, height: 1, background: C.border }} />
    </div>
  );
}

function Pill({ text, color, light }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", padding: "2px 7px",
      borderRadius: 3, background: light, color, border: `1px solid ${color}33`, whiteSpace: "nowrap",
    }}>{text}</span>
  );
}

const BLOCK_STYLES = {
  key:  { bg: C.goldL,   border: "#C9A84C55", titleColor: "#8a6d1f", icon: "🔑" },
  warn: { bg: C.redL,    border: "#DC262633", titleColor: C.red,     icon: "⚠️" },
  tip:  { bg: C.blueL,   border: "#1D4ED833", titleColor: C.blue,    icon: "💡" },
  note: { bg: C.purpleL, border: "#7C3AED33", titleColor: C.purple,  icon: "📌" },
};

function ContentBlock({ block }) {
  if (block.type === "para") {
    return <p style={{ fontSize: 13, lineHeight: 1.75, color: C.dark, margin: "12px 0" }}>{block.text}</p>;
  }
  const s = BLOCK_STYLES[block.type];
  return (
    <div style={{
      borderRadius: 9, padding: "13px 15px", margin: "14px 0", display: "flex", gap: 10,
      alignItems: "flex-start", border: `1px solid ${s.border}`, background: s.bg,
    }}>
      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{s.icon}</span>
      <div>
        <span style={{ display: "block", fontWeight: 800, fontSize: 10.5, letterSpacing: "0.05em", textTransform: "uppercase", color: s.titleColor, marginBottom: 4 }}>
          {block.title}
        </span>
        <span style={{ fontSize: 12.5, lineHeight: 1.65, color: C.dark }}>{block.text}</span>
      </div>
    </div>
  );
}

function ObjectivesBox({ objectives }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.gold}`, borderRadius: 8, padding: "14px 16px", margin: "14px 0" }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: C.gold, textTransform: "uppercase", marginBottom: 8 }}>
        Learning Objectives
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {objectives.map((o, i) => (
          <li key={i} style={{ fontSize: 12.5, color: C.dark, marginBottom: 5, paddingLeft: 18, position: "relative", lineHeight: 1.5 }}>
            <span style={{ position: "absolute", left: 0, color: C.green, fontWeight: 800 }}>✓</span>{o}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PracticeQuestion({ p, index }) {
  const [chosen, setChosen] = useState(null);

  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, padding: "15px 16px", margin: "12px 0" }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: C.muted, letterSpacing: "0.06em", marginBottom: 6 }}>
        PRACTICE QUESTION {index + 1}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 10, lineHeight: 1.5 }}>{p.q}</div>
      {p.options.map((opt, i) => {
        let bg = C.offwhite, border = C.border, color = C.dark, weight = 400;
        if (chosen !== null) {
          if (i === p.correct) { bg = C.greenL; border = C.green; color = "#065f46"; weight = 700; }
          else if (i === chosen) { bg = C.redL; border = C.red; color = "#991b1b"; weight = 700; }
        }
        return (
          <button
            key={i}
            disabled={chosen !== null}
            onClick={() => setChosen(i)}
            style={{
              display: "block", width: "100%", textAlign: "left", padding: "8px 12px", marginBottom: 6,
              borderRadius: 6, border: `1px solid ${border}`, background: bg, color, fontWeight: weight,
              fontSize: 12.5, cursor: chosen === null ? "pointer" : "default", fontFamily: "inherit",
            }}
          >
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {chosen !== null && (
        <div style={{ fontSize: 12, color: C.dark, background: C.slate, borderRadius: 6, padding: "10px 12px", marginTop: 8, lineHeight: 1.6 }}>
          <b>Explanation:</b> {p.explanation}
        </div>
      )}
    </div>
  );
}

function StatCard({ s, groupColor }) {
  const flag = STATE_FLAGS[s.code] || "🏳️";
  return (
    <div
      style={{
        background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16,
        borderTop: `3px solid ${groupColor}`, display: "flex", flexDirection: "column", gap: 10,
        transition: "box-shadow .15s, transform .15s", cursor: "default",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 6px 24px ${groupColor}25`; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>{flag}</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, color: C.navy, letterSpacing: "-0.02em", lineHeight: 1 }}>{s.code}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2, fontWeight: 500 }}>{s.name}</div>
          </div>
        </div>
        {s.alert && <span style={{ fontSize: 16 }}>⚠️</span>}
      </div>

      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px", padding: "10px 0",
        borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`,
      }}>
        {[["W", s.width], ["H", s.height], ["Ins.", s.ins], ["Night", s.night]].map(([l, v]) => (
          <div key={l}>
            <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, display: "block" }}>{l}</span>
            <span style={{ fontSize: 13, color: C.dark, fontWeight: 700 }}>{v}</span>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: s.alert ? C.red : groupColor, display: "flex", alignItems: "center", gap: 4 }}>
        <span>{s.alert ? "⚠️" : "✓"}</span>{s.cert}
      </div>
    </div>
  );
}

function GroupSection({ groupId }) {
  const g = GROUP_STATES[groupId];
  return (
    <div>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "22px 0 18px" }}>
        <div style={{ width: 44, height: 44, borderRadius: 8, background: g.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
          {g.icon}
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 17, fontWeight: 800, color: C.navy, letterSpacing: "-0.02em" }}>{g.label}</span>
            <Pill text={g.sublabel} color={g.color} light={g.colorL} />
            <Pill text={`${g.states.length} states`} color={C.muted} light={C.slate} />
          </div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, maxWidth: 640 }}>{g.description}</div>
        </div>
      </div>

      <div style={{
        padding: "10px 14px", borderRadius: 7, background: `${g.color}11`, border: `1px solid ${g.color}33`,
        fontSize: 11, color: C.dark, lineHeight: 1.6, marginBottom: 16, display: "flex", gap: 8, alignItems: "flex-start",
      }}>
        <span style={{ color: g.color, fontWeight: 800, flexShrink: 0 }}>KEY RULE:</span>{g.keyRule}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))", gap: 10 }}>
        {g.states.map((s) => <StatCard key={s.code} s={s} groupColor={g.color} />)}
      </div>

      {g.states.some((s) => s.alert) && (
        <div style={{
          marginTop: 14, padding: "12px 16px", borderRadius: 7, background: C.redL, border: `1px solid ${C.red}33`,
          display: "flex", gap: 10, alignItems: "flex-start",
        }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
          <div style={{ fontSize: 11, color: C.dark, lineHeight: 1.6 }}>
            <strong style={{ color: C.red }}>HIGH ALERT states in this group:</strong>{" "}
            {g.states.filter((s) => s.alert).map((s) => s.code).join(", ")} — these states impose requirements
            beyond the group standard. Verify individually before crossing their borders.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SECTION: OVERVIEW ─────────────────────────────────────────────
function Overview({ onJump }) {
  const steps = [
    { step: "1", label: "Am I PPCA certified?", res: "→ Sufficient in 14 PEVO states — but NOT Washington", color: C.green },
    { step: "2", label: "Am I operating in Washington?", res: "→ WSDOT Pilot Car Operator Permit required in addition to PEVO", color: C.blue },
    { step: "3", label: "Does the load exceed 16 ft wide or 200,000 lbs?", res: "→ Washington State Patrol escort required", color: C.amber },
    { step: "4", label: "Does the route include a ferry or mountain pass?", res: "→ Verify WSF authorization / seasonal closures first", color: C.muted },
  ];

  return (
    <div>
      <SectionEyebrow text="WHERE WASHINGTON FITS IN THE 4-GROUP FRAMEWORK" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 16 }}>
        {GROUPS4.map((g, i) => (
          <div
            key={i}
            onClick={() => g.id && onJump(g.id)}
            style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${C.border}`, cursor: g.id ? "pointer" : "default" }}
          >
            <div style={{ background: g.color, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, color: C.white, fontWeight: 800, fontSize: 12 }}>
              {g.icon} {g.label}
            </div>
            <div style={{ background: g.colorL, padding: "10px 14px" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: g.color, lineHeight: 1 }}>{g.states.split(",").length}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>{g.sub}</div>
              {g.id && <div style={{ fontSize: 9, color: g.color, marginTop: 6, fontWeight: 700 }}>View states →</div>}
            </div>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.75, color: C.dark }}>
        Washington belongs to the <b>8-State Group</b> — states requiring a separate state-issued Pilot Car Operator
        Permit in addition to national PEVO certification. WSDOT Commercial Vehicle Services (CVS) issues and
        administers the Washington-specific permit.
      </p>

      <div style={{ background: C.slate, borderRadius: 12, padding: 16, border: `1px solid ${C.border}`, marginTop: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 14, textAlign: "center" }}>
          Quick Decision Flow — Washington
        </div>
        {steps.map((s, i) => (
          <div key={i} style={{
            display: "flex", gap: 12, alignItems: "flex-start",
            paddingBottom: i < steps.length - 1 ? 14 : 0, marginBottom: i < steps.length - 1 ? 14 : 0,
            borderBottom: i < steps.length - 1 ? `1px solid ${C.border}` : "none",
          }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: s.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, color: "#fff", flexShrink: 0 }}>
              {s.step}
            </div>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.navy, marginBottom: 2 }}>{s.label}</div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: s.color }}>{s.res}</div>
            </div>
          </div>
        ))}
      </div>

      <SectionEyebrow text="14-MODULE CURRICULUM" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 10 }}>
        {MODULES.map((m) => (
          <div
            key={m.num}
            onClick={() => onJump("m" + m.num)}
            style={{ borderRadius: 12, overflow: "hidden", border: `1px solid ${C.border}`, cursor: "pointer" }}
          >
            <div style={{ background: C.navy, padding: "10px 14px", color: C.gold, fontWeight: 800, fontSize: 12 }}>
              {m.icon} Module {m.num}
            </div>
            <div style={{ background: C.white, padding: "10px 14px" }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: C.navy, lineHeight: 1.4 }}>{m.title}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>{m.time}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SECTION: MODULE PAGE ──────────────────────────────────────────
function ModulePage({ m }) {
  return (
    <div>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "22px 0 8px" }}>
        <div style={{ width: 46, height: 46, borderRadius: 10, background: C.navy, color: C.gold, fontWeight: 900, fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {m.icon}
        </div>
        <div>
          <h2 style={{ fontSize: 19, fontWeight: 800, color: C.navy, letterSpacing: "-0.02em", margin: "0 0 4px" }}>
            Module {m.num} — {m.title}
          </h2>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>Estimated study time · {m.time}</div>
        </div>
      </div>

      <ObjectivesBox objectives={m.objectives} />

      {m.blocks.map((b, i) => <ContentBlock key={i} block={b} />)}

      <SectionEyebrow text="PRACTICE QUESTIONS" />
      {m.practice.map((p, i) => <PracticeQuestion key={i} p={p} index={i} />)}
    </div>
  );
}

// ─── SECTION: CHECKLIST INFOGRAPHIC ────────────────────────────────
function ChecklistInfographic() {
  const [checked, setChecked] = useState({});
  const half = Math.ceil(EQUIPMENT.length / 2);
  const col1 = EQUIPMENT.slice(0, half);
  const col2 = EQUIPMENT.slice(half);

  const renderCol = (items, offset) => (
    <div style={{ padding: 20 }}>
      {items.map((e, i) => {
        const gi = i + offset;
        const on = checked[gi];
        return (
          <div key={gi} style={{ display: "flex", gap: 10, padding: "11px 0", borderBottom: `1px solid ${C.border}`, alignItems: "flex-start" }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: C.goldL, border: "1px solid #C9A84C55", color: "#8a6d1f", fontWeight: 800, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {gi + 1}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: C.navy, marginBottom: 3 }}>{e.icon} {e.name}</div>
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{e.spec}</div>
              <span style={{ fontSize: 9, color: C.gold, fontWeight: 700, marginTop: 3, display: "inline-block" }}>{e.ref}</span>
            </div>
            <div
              onClick={() => setChecked((p) => ({ ...p, [gi]: !p[gi] }))}
              style={{
                width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginLeft: "auto", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff",
                background: on ? C.green : "transparent", border: `1.5px solid ${on ? C.green : C.border}`,
              }}
            >
              {on ? "✓" : ""}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ background: "linear-gradient(135deg,#0d1f3c,#142a4d)", borderRadius: "14px 14px 0 0", padding: "26px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.gold, letterSpacing: "0.12em", marginBottom: 8 }}>REQUIRED EQUIPMENT CHECKLIST</div>
          <h2 style={{ color: C.white, fontSize: 26, fontWeight: 900, margin: "0 0 8px", letterSpacing: "-0.02em" }}>Checklist — Washington</h2>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 12.5, margin: 0, maxWidth: 520, lineHeight: 1.6 }}>
            All equipment must be in good working condition and clearly visible/accessible. Verify requirements with your permit before each move.
          </p>
        </div>
        <div style={{ width: 78, height: 78, borderRadius: 14, background: C.gold, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: C.navy, flexShrink: 0 }}>
          <div style={{ fontSize: 28 }}>🌲</div>
          <div style={{ fontWeight: 900, fontSize: 15, marginTop: 2 }}>WA</div>
          <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: ".05em" }}>WSDOT</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 250px", background: C.white, border: `1px solid ${C.border}`, borderTop: "none" }}>
        {renderCol(col1, 0)}
        {renderCol(col2, half)}
        <div style={{ background: C.navy, color: C.white, padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", color: C.gold, marginBottom: 12 }}>
            ⚠️ IMPORTANT
          </div>
          <ul style={{ listStyle: "none", margin: "0 0 18px", padding: 0 }}>
            {[
              "Carry all required equipment at all times — WSP may inspect at any time",
              "Equipment must be in good working order before departure",
              "Inspection can be conducted at any time on the highway",
              "Non-compliance: fines, delays, or permit revocation",
            ].map((li, i) => (
              <li key={i} style={{ fontSize: 11.5, lineHeight: 1.6, marginBottom: 10, paddingLeft: 16, position: "relative", color: "rgba(255,255,255,0.85)" }}>
                <span style={{ position: "absolute", left: 0, color: C.gold }}>▸</span>{li}
              </li>
            ))}
          </ul>
          {[["Width", "14'6\""], ["Height", "14'6\""], ["Insurance", "$500K CSL"]].map(([l, v]) => (
            <div key={l} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: ".06em" }}>{l}</div>
              <div style={{ fontSize: 16, fontWeight: 900, color: C.gold }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", border: `1px solid ${C.border}`, borderTop: "none", background: C.white }}>
        {GROUPS4.map((g, i) => (
          <div key={i} style={{ padding: "14px 16px", borderRight: i < GROUPS4.length - 1 ? `1px solid ${C.border}` : "none" }}>
            <div style={{ fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", gap: 6, marginBottom: 4, color: g.color }}>
              {g.icon} {g.label}
            </div>
            <div style={{ fontSize: 9, color: C.muted, marginBottom: 8 }}>{g.sub}</div>
            <div style={{ fontSize: 10.5, color: C.dark, lineHeight: 1.6 }}>{g.states}</div>
          </div>
        ))}
      </div>

      <div style={{ background: C.navy, borderRadius: "0 0 14px 14px", padding: "12px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", border: `1px solid ${C.border}`, borderTop: "none" }}>
        <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.8)", fontWeight: 700 }}>
          ALWAYS VERIFY CURRENT REQUIREMENTS WITH <span style={{ color: C.gold }}>WSDOT</span> AND YOUR PERMIT BEFORE EACH MOVE.
        </div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", textAlign: "right" }}>
          Washington WSDOT<br />wsdot.wa.gov · (360) 705-7000
        </div>
      </div>
    </div>
  );
}

// ─── SECTION: QUICK REFERENCE ───────────────────────────────────────
function QuickReference() {
  return (
    <div>
      <SectionEyebrow text="MASTER QUICK REFERENCE — WASHINGTON" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14, marginTop: 14 }}>
        {QUICKREF.map((card, i) => (
          <div key={i} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ background: C.navy, color: C.gold, fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", padding: "9px 14px" }}>
              {card.h.toUpperCase()}
            </div>
            {card.rows.map(([k, v], j) => (
              <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "8px 14px", borderBottom: j < card.rows.length - 1 ? `1px solid ${C.border}` : "none", fontSize: 12 }}>
                <span style={{ color: C.muted }}>{k}</span>
                <span style={{ fontWeight: 800, color: C.navy }}>{v}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────────
export default function PPCAWashingtonCrashCourse({ isDarkMode, toggleTheme }) {
  const tabs = [
    { id: "overview", label: "Overview", icon: "🗂️" },
    { id: "pevo", label: "14 PEVO", icon: "🛡️" },
    { id: "eight", label: "8-State", icon: "🔷" },
    { id: "standalone", label: "Stand-Alone", icon: "⭐" },
    { id: "checklist", label: "Checklist", icon: "✅" },
    ...MODULES.map((m) => ({ id: "m" + m.num, label: "M" + m.num, icon: m.icon })),
    { id: "quickref", label: "Quick Ref", icon: "📊" },
  ];
  const [activeTab, setActiveTab] = useState("overview");

  const [localDark, setLocalDark] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("theme") === "dark"
  );
  const D = isDarkMode !== undefined ? isDarkMode : localDark;
  const toggleDark = () => {
    const next = !localDark;
    setLocalDark(next);
    localStorage.setItem("theme", next ? "dark" : "light");
    document.documentElement.classList.toggle("dark", next);
    if (toggleTheme) toggleTheme();
  };

  const activeModule = MODULES.find((m) => "m" + m.num === activeTab);

  return (
    <div style={{
      fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
      background: D ? "#0d1f3c" : C.offwhite, minHeight: "100vh",
      color: D ? "#f0f4f8" : C.dark, transition: "background .3s",
    }}>
      <Navbar isDarkMode={D} toggleTheme={toggleDark} />

      {/* ── SUB-TAB BAR ── */}
      <div style={{ background: D ? "#0d1f3c" : C.navy, borderBottom: `2px solid ${C.gold}`, position: "sticky", top: 62, zIndex: 200 }}>
        <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 16px" }}>
          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", gap: 0, overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  style={{
                    padding: "11px 14px", border: "none", cursor: "pointer",
                    background: activeTab === t.id ? "rgba(201,168,76,0.15)" : "transparent",
                    color: activeTab === t.id ? C.gold : `${C.white}70`,
                    fontWeight: activeTab === t.id ? 800 : 500, fontSize: 12.5,
                    display: "flex", alignItems: "center", gap: 5,
                    borderBottom: `3px solid ${activeTab === t.id ? C.gold : "transparent"}`,
                    borderRadius: activeTab === t.id ? "6px 6px 0 0" : 0,
                    transition: "all .15s", whiteSpace: "nowrap", fontFamily: "inherit", flexShrink: 0,
                  }}
                >
                  <span style={{ fontSize: 13 }}>{t.icon}</span>{t.label}
                </button>
              ))}
            </div>
            <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 40, background: `linear-gradient(to right, transparent, ${D ? "#0d1f3c" : C.navy})`, pointerEvents: "none" }} />
          </div>
        </div>
      </div>

      {/* ── HERO STRIP ── */}
      <div style={{ background: "linear-gradient(135deg, #0d1f3c 0%, #142a4d 100%)", borderBottom: `1px solid ${C.gold}33`, padding: "28px 24px" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "clamp(16px, 4vw, 40px)", alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.gold, letterSpacing: "0.12em", marginBottom: 10 }}>
                PPCA PRE-EXAM CRASH COURSE
              </div>
              <h1 style={{ fontSize: 28, fontWeight: 900, color: C.white, margin: "0 0 8px", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
                Washington <span style={{ color: C.gold }}>WSDOT</span> Pilot / Escort Vehicle Certification
              </h1>
              <p style={{ fontSize: 13, color: `${C.white}99`, margin: 0, lineHeight: 1.6 }}>
                14 modules · Federal + Washington-specific regulations · Ferry, mountain pass &amp; WSP escort rules
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[{ n: "14", l: "Modules" }, { n: "6h 45m", l: "Study Time" }, { n: "500+", l: "Question Bank" }, { n: "70%", l: "Passing Score" }].map((s, i) => (
                <div key={i} style={{ padding: "10px 14px", borderRadius: 7, background: `${C.white}08`, border: `1px solid ${C.gold}22`, textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: C.gold, lineHeight: 1 }}>{s.n}</div>
                  <div style={{ fontSize: 9, color: `${C.white}77`, marginTop: 3 }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 clamp(12px, 4vw, 24px) 48px" }}>
        {activeTab === "overview" && <Overview onJump={setActiveTab} />}
        {activeTab === "pevo" && <GroupSection groupId="pevo" />}
        {activeTab === "eight" && <GroupSection groupId="eight" />}
        {activeTab === "standalone" && <GroupSection groupId="standalone" />}
        {activeTab === "checklist" && <ChecklistInfographic />}
        {activeTab === "quickref" && <QuickReference />}
        {activeModule && <ModulePage m={activeModule} />}

        <div style={{ marginTop: 32, padding: "12px 16px", borderRadius: 7, background: C.redL, border: `1px solid ${C.red}22`, fontSize: 10, color: C.red, lineHeight: 1.6 }}>
          ⚠️ <strong>IMPORTANT:</strong> This PPCA training content is for educational pre-exam preparation only.
          It does not constitute state certification or authorization to operate as a pilot/escort vehicle operator.
          Always verify current requirements with WSDOT before every permitted move. Regulations change — confirm
          before each operation. © {new Date().getFullYear()} PilotLinkUSA
        </div>
      </div>
    </div>
  );
}
